import { Component, inject, OnInit, signal, effect, computed, input, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DashboardLayoutEditorComponent } from '../../../../shared/components/dashboard/dashboard-layout-editor.component';
import { ApiService } from '../../../../core/services/api.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import {
  DashboardPageKey,
  CoolCreditDashboardSectionId,
  CoolCreditDashboardSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilterByWidgetKeyMap, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  getLiveSectionColClass,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DatePickerComponent, type DateRangePickerOutput } from '../../../../shared/components/date-picker/date-picker.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

interface SectionLayoutItem {
  id: CoolCreditDashboardSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

@Component({
  standalone: true,
  selector: 'app-cool-credit-dashboard',
  imports: [CommonModule, DashboardLayoutEditorComponent, DatePickerComponent, IndividualToasterComponent],
  templateUrl: './cool-credit-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cool-credit-dashboard.component.scss',
})
export class CoolCreditDashboardComponent implements OnInit {
  private apiService = inject(ApiService);
  private loaderService = inject(LoaderService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.COOL_CREDIT_DASHBOARD;
  readonly SectionId = CoolCreditDashboardSectionId;
  readonly SECTIONS = CoolCreditDashboardSectionDef;

  readonly ALL_SECTION_IDS: CoolCreditDashboardSectionId[] = [
    CoolCreditDashboardSectionId.BUSINESS_GROWTH,
    CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS,
    CoolCreditDashboardSectionId.PRODUCT_USAGE,
    CoolCreditDashboardSectionId.MONETIZATION_REVENUE,
    CoolCreditDashboardSectionId.PLAN_LIFECYCLE,
    CoolCreditDashboardSectionId.DISPUTES,
  ];

  isLoading = signal(false);
  ccMetrics: any;
  businessGrowth: any = [];
  activationAndCalls: any = [];
  productUsage: any = [];
  memorizationAndRevenue: any = [];
  planLifeCycle: any = [];
  disputes: any = [];
  newOnBoardingFlow: any = [];

  public dateRange = signal((() => {
    const today = dayjs().tz(LA_TIMEZONE);
    const sevenDaysAgo = today.subtract(6, 'day');
    return {
      startDate: sevenDaysAgo.format('MM-DD-YYYY'),
      endDate: today.format('MM-DD-YYYY'),
    };
  })());

  constructor() {
    this._initLayout();
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<CoolCreditDashboardSectionId>();
          keys.forEach((k) => {
            const mapped = this.WIDGET_KEY_TO_SECTION.get(k);
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(CoolCreditDashboardSectionId).includes(k as CoolCreditDashboardSectionId)) {
              pinned.add(k as CoolCreditDashboardSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<CoolCreditDashboardSectionId, number>();
            for (const sec of Object.values(CoolCreditDashboardSectionId)) {
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  ngOnInit(): void {
    if (this.mixedMode()) {
      this.fetchStats();
    }
  }

  readonly layoutMap = signal<Record<CoolCreditDashboardSectionId, SectionLayoutItem>>(
    {} as Record<CoolCreditDashboardSectionId, SectionLayoutItem>,
  );

  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  showSection(sectionId: CoolCreditDashboardSectionId): boolean {
    if (this.mixedMode()) {
      return this.embeddedVisibleSections().some((s) => s.id === sectionId);
    }
    const map = this.layoutMap();
    if (!Object.keys(map).length) return true;
    if (!map[sectionId]) return true;
    return map[sectionId].visible !== false;
  }

  sectionOrder(sectionId: CoolCreditDashboardSectionId): number {
    return this.layoutMap()[sectionId]?.order ?? 0;
  }

  getSectionColClass(sectionId: CoolCreditDashboardSectionId): string {
    const span =
      this.layoutMap()[sectionId]?.colSpan ??
      this.DEFAULT_COL_SPAN[sectionId] ??
      4;
    return getLiveSectionColClass(span);
  }

  hasVisibleCards(): boolean {
    return this.ALL_SECTION_IDS.some((id) => this.showSection(id));
  }

  /** Fixed height on the standalone page; stretch to the My Dashboard tile in mixed mode. */
  sectionShellHeightClass(): string {
    return this.mixedMode() ? 'h-full min-h-[300px]' : 'h-96';
  }

  readonly pinnedSections = signal<Set<CoolCreditDashboardSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<CoolCreditDashboardSectionId>>(new Set());

  readonly pinnableWidgetIds = signal<Map<CoolCreditDashboardSectionId, number>>(
    new Map(),
  );

  /**
   * Frontend section id (`cc_*`) → backend catalog `widget_key`
   * (`co_cc_*`) mapping. Backend catalog under product Cool Credit
   * (`pages.id = 39`, slug `analytical-dashboard`) registers widgets
   * with `co_cc_` prefixes; `cc_activation_and_calls` differs from the
   * backend key `co_cc_activation_calls`.
   */
  private readonly SECTION_WIDGET_KEYS: Partial<Record<CoolCreditDashboardSectionId, string>> = {
    [CoolCreditDashboardSectionId.BUSINESS_GROWTH]: 'co_cc_business_growth',
    [CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS]: 'co_cc_activation_calls',
    [CoolCreditDashboardSectionId.PRODUCT_USAGE]: 'co_cc_product_usage',
    [CoolCreditDashboardSectionId.MONETIZATION_REVENUE]: 'co_cc_monetization_revenue',
    [CoolCreditDashboardSectionId.PLAN_LIFECYCLE]: 'co_cc_plan_lifecycle',
    [CoolCreditDashboardSectionId.DISPUTES]: 'co_cc_disputes',
  };

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, CoolCreditDashboardSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        CoolCreditDashboardSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  isAddedToCustomDashboard(sectionId: CoolCreditDashboardSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: CoolCreditDashboardSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  addToCustomDashboard(
    sec: { id: CoolCreditDashboardSectionId } | CoolCreditDashboardSectionId,
  ): void {
    // Accept either the full section layout item (templates that iterate
    // over `orderedSections()` pass `sec`) or the raw section id (templates
    // that fan out via `<ng-template let-sectionId>`). Both call sites flow
    // into the same `CustomDashboardPinService.toggleSection(...)` PATCH
    // request below — only the call signature was unified.
    const sectionId =
      typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );
        if (resolvedPageId == null) return;

        this.effectiveCatalogPageId.set(resolvedPageId);

        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(CoolCreditDashboardSectionId)) {
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  private _layoutSnapshot: Record<CoolCreditDashboardSectionId, SectionLayoutItem> | null =
    null;

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_cool_credit_dashboard_layout_v1';

  private readonly DEFAULT_COL_SPAN: Record<CoolCreditDashboardSectionId, number> = {
    [CoolCreditDashboardSectionId.BUSINESS_GROWTH]: 4,
    [CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS]: 4,
    [CoolCreditDashboardSectionId.PRODUCT_USAGE]: 4,
    [CoolCreditDashboardSectionId.MONETIZATION_REVENUE]: 4,
    [CoolCreditDashboardSectionId.PLAN_LIFECYCLE]: 4,
    [CoolCreditDashboardSectionId.DISPUTES]: 4,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    this.editorItems.set(
      this.orderedSections().map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
        defaultColSpan: this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as CoolCreditDashboardSectionId] ?? 4,
        ),
      })),
    );
    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: CoolCreditDashboardSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    this.editorItems.set(
      this.SECTIONS.map((s, idx) => {
        const defaultSpan = this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as CoolCreditDashboardSectionId] ?? 4,
        );
        return {
          id: s.id as CoolCreditDashboardSectionId,
          title: s.title,
          visible: true,
          colSpan: defaultSpan,
          order: idx,
          defaultColSpan: defaultSpan,
        };
      }),
    );
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via {@link CustomizeLayoutBatchService} — same flow as
   * Practina / Lendee dashboards.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
            (p: any) => String(p?.id) === String(productId),
          )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[CoolCreditDashboard] No catalog page found for ` +
        `"${this.PAGE_KEY}" (active product=${productId}). ` +
        `Catalog advertised pages for this product: ` +
        `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
        `If the correct page is in the list above, add a matching ` +
        `substring to CATALOG_PAGE_HINTS[COOL_CREDIT_DASHBOARD] ` +
        `in dashboard-embed.helpers.ts.`,
    );
  }

  decreaseEditorCardColSpan(id: CoolCreditDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[i.id] ?? 4);
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: CoolCreditDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[item.id] ?? 4);
    return this.COL_SPAN_STEPS[idx - 1] >= minSpan;
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  private _buildLayoutFromEditor(): Record<CoolCreditDashboardSectionId, SectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS as readonly { id: CoolCreditDashboardSectionId }[],
      this.DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<CoolCreditDashboardSectionId, SectionLayoutItem> =
      {} as Record<CoolCreditDashboardSectionId, SectionLayoutItem>;
    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as CoolCreditDashboardSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible !== false,
        colSpan: this._normalizeColSpan(storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 4),
        order: Number.isFinite(storedItem?.order) ? Number(storedItem!.order) : idx,
      };
    });
    if (!Object.values(layout).some((s) => s.visible)) {
      this.SECTIONS.forEach((s, idx) => {
        const sid = s.id as CoolCreditDashboardSectionId;
        layout[sid] = {
          id: sid,
          visible: true,
          colSpan: this._normalizeColSpan(this.DEFAULT_COL_SPAN[sid] ?? 4),
          order: idx,
        };
      });
      this._saveLayoutToStorage(layout);
    }
    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      const hasKnown = this.SECTIONS.some((s) => parsed[s.id] != null);
      return hasKnown ? parsed : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(layout: Record<CoolCreditDashboardSectionId, SectionLayoutItem>): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // ignore
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  private _fetchGeneration = 0;

  fetchStats() {
    this.resetData();
    const generation = ++this._fetchGeneration;
    this.isLoading.set(true);
    const payload = {
      start_date: this.dateRange().startDate,
      end_date: this.dateRange().endDate,
      metrics: [],
    };

    this.apiService
      .post('adm/admin/v1/stats/weekly-summary', payload, {
        addAccessToken: false,
        productServerName: 'cool-credit',
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (generation !== this._fetchGeneration) return;
          const metrics = response?.data?.metrics;
          if (Array.isArray(metrics) && metrics.length > 0) {
            this.ccMetrics = metrics;
            this.categorizeData();
          }
          this.isLoading.set(false);
        },
        error: (error: any) => {
          if (generation !== this._fetchGeneration) return;
          console.error('API Error:', error);
          this.isLoading.set(false);
        },
      });
  }

  categorizeData() {
    this.ccMetrics.forEach((elem: any) => {
      if (['registered_users_count', 'cbc_filled_all', 'cbc_filled_cohort', 'health_report_users'].includes(elem.id)) {
        this.businessGrowth.push(elem);
      } else if (['scheduled_calls_all', 'scheduled_calls_cohort', 'calls_from_dispute_all', 'calls_from_dispute_cohort', 'call_now_all', 'call_now_cohort', 'call_now_dash', 'call_now_pre_sim', 'call_now_post_sim', 'call_now_dispute', 'call_now_more'].includes(elem.id)) {
        this.activationAndCalls.push(elem);
      } else if (['users_refreshed_report', 'through_sim', 'through_sim_and_purchased', 'through_sim_without_plan', 'without_sim_with_plan'].includes(elem.id)) {
        this.productUsage.push(elem);
      } else if (['plan_purchased_count', 'plan_purchases_cohort', 'plan_amount_starter', 'plan_amount_essential', 'plan_amount_premium', 'plan_amount_ultimate', 'plan_amount_boost_only', 'plan_amount_all', 'total_purchase_amount_cohort'].includes(elem.id)) {
        this.memorizationAndRevenue.push(elem);
      } else if (['plan_cancelled_count', 'plan_expired_count', 'plan_paused_count'].includes(elem.id)) {
        this.planLifeCycle.push(elem);
      } else if (['disputes_created_count', 'disputes_created_cohort'].includes(elem.id)) {
        this.disputes.push(elem);
      }
    });
  }

  onDateRangeChange(output: DateRangePickerOutput): void {
    const startDate = dayjs.utc(output.start_date).tz(LA_TIMEZONE);
    const endDate = dayjs.utc(output.end_date).tz(LA_TIMEZONE);
    this.dateRange.set({
      startDate: startDate.format('MM-DD-YYYY'),
      endDate: endDate.format('MM-DD-YYYY'),
    });
    this.fetchStats();
  }

  resetData() {
    this.ccMetrics = [];
    this.businessGrowth = [];
    this.activationAndCalls = [];
    this.productUsage = [];
    this.memorizationAndRevenue = [];
    this.planLifeCycle = [];
    this.disputes = [];
    this.newOnBoardingFlow = [];
  }

  testLoader() {
    this.loaderService.show({ message: 'Testing global loader functionality...' });
    setTimeout(() => this.loaderService.hide(), 3000);
  }
}
